import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import debounce from 'lodash.debounce';
import { TextField, TextFieldProps } from '@mui/material';
import { SearchIcon } from '../../../assets';
import { useGetQueryParams, useUpdatePageQueryParams } from '../../../hooks';
import { useStyles } from './inputs.styles';
import { InputChangeEventType } from '../../../types';

interface SearchInputProps {
  queryParam?: string;
  onSearchOverride?: (search: string) => void;
}

export const SearchInput = function SearchInput({
  label = 'Search',
  queryParam = 'search',
  onSearchOverride,
  className,
  slotProps: _slotProps,
  ...rest
}: TextFieldProps & SearchInputProps) {
  const { classes, cx } = useStyles();
  const updatePageQueryParams = useUpdatePageQueryParams();
  const { [queryParam]: search, 'custom-view-id': customViewId } =
    useGetQueryParams();

  const { input = {}, inputLabel = {}, ...slotProps } = _slotProps || {};

  const [inputValue, setInputValue] = useState(search || '');
  const latestCallRef = useRef(0); // Track the latest debounce call

  // See here why we use useMemo and not useCallback
  // https://github.com/facebook/react/issues/19240#issuecomment-652945246
  const debounceFilter = useMemo(
    () =>
      debounce((value: string, callId: number) => {
        if (callId !== latestCallRef.current) return; // Discard stale calls
        if (typeof onSearchOverride === 'function') {
          onSearchOverride(value);
        } else {
          updatePageQueryParams({ [queryParam]: value || null });
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParam, updatePageQueryParams],
  );

  const onFilterChange = useCallback(
    (e: InputChangeEventType) => {
      const { value } = e.target;
      const currentCallId = ++latestCallRef.current; // Increment for each change
      debounceFilter(value, currentCallId);
      setInputValue(value);
    },
    [debounceFilter],
  );

  // keep the input synched with the query param
  useEffect(() => {
    if (customViewId && search !== inputValue) {
      setInputValue(search || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <TextField
      variant='standard'
      label={label}
      name='searchText'
      onChange={onFilterChange}
      value={inputValue}
      autoComplete='off'
      slotProps={{
        input: {
          classes: {
            root: cx(classes.searchRoot, className),
          },
          endAdornment: <SearchIcon className={classes.searchIcon} />,
          ...input,
        },

        inputLabel: { classes: { root: classes.label }, ...inputLabel },
        ...slotProps,
      }}
      {...rest}
    />
  );
};
